import { useMemo } from "react";
import { ALL_CLIENTS } from "AppConstants";
import { toManagerClientOptions, mergeClientOptionsById } from "utils/clientOptions";
import { deriveSubtreeClients, deriveSubtreeL1Options } from "utils/managerSubtree";

/**
 * The Client and Managers options a logged-in manager's filters offer, derived from their
 * rolled-up subtree plus their own session assignments.
 *
 * The subtree alone misses an assignment held outside the reporting hierarchy, and the
 * session list alone misses L1 children — so the client list is the union of both, and any
 * client whose rows can appear in a report stays selectable. The L1 options exclude the
 * manager's own records, which their "(Me)" option already covers.
 *
 * Pure derivation: the caller still owns loading `subtreeManagers` and `managerClients`.
 * @since NEXT_VERSION
 */
export const useManagerScopeOptions = ({
  subtreeManagers = [],
  managerClients = [],
  selectedClient = ALL_CLIENTS,
} = {}) => {
  // deriveSubtreeClients already yields the manager-record shape, so both sides go
  // through the same mapper and label identically.
  const clientOptions = useMemo(
    () =>
      mergeClientOptionsById(
        toManagerClientOptions(deriveSubtreeClients(subtreeManagers)),
        toManagerClientOptions(managerClients)
      ),
    [subtreeManagers, managerClients]
  );

  const subtreeL1Options = useMemo(
    () =>
      deriveSubtreeL1Options(
        subtreeManagers,
        managerClients.map((c) => c.id),
        selectedClient,
        ALL_CLIENTS
      ),
    [subtreeManagers, managerClients, selectedClient]
  );

  return { clientOptions, subtreeL1Options };
};

export default useManagerScopeOptions;
